import { useContext, useState } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import CustomTooltip from "../CustomTooltip";
import KycDetailsModal from "../KycDetailsModal";
import { AppContext } from "../../Context/AppContext";
import { apiPostRequest } from "../../Helpers/Requests";
import endpoints from "../../Helpers/Endpoints";
import {
  KYC_STATUS,
  hasKycNotice,
  needsKycDetails,
} from "../../Constants/kycStatus";

// The one place the supplier's KYC state is written down. It sits in the
// sidebar under the business name, and again beside every control it blocks,
// so a disabled button always carries its own reason.
//
// In the first state the supplier has something to DO — fill in Grow's form —
// so there the chip is a button that opens it. In the others it only explains.
// Nothing is shown once the supplier is approved: they were told by
// notification, and a permanent "approved" badge is noise from then on.

const TONE = {
  [KYC_STATUS.DETAILS_REQUIRED]: {
    color: "rgba(37, 124, 109, 1)",
    background: "rgba(37, 124, 109, 0.12)",
  },
  [KYC_STATUS.PENDING]: {
    color: "rgba(223, 131, 80, 1)",
    background: "rgba(235, 150, 96, 0.15)",
  },
  [KYC_STATUS.REJECTED]: {
    color: "rgba(196, 60, 60, 1)",
    background: "rgba(196, 60, 60, 0.12)",
  },
};

const chipBase = {
  height: "18px",
  padding: "0 8px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  whiteSpace: "nowrap",
  borderRadius: "12px",
  fontSize: "11px",
  fontWeight: 500,
  cursor: "default",
};

const COPY_KEY = {
  [KYC_STATUS.DETAILS_REQUIRED]: "details",
  [KYC_STATUS.PENDING]: "pending",
  [KYC_STATUS.REJECTED]: "rejected",
};

/**
 * With no children: the chip itself, with its explanation on hover.
 * With children (a disabled control): only the explanation, wrapped around
 * them — used where a row has no room for a chip without widening the table.
 */
const KycStatusChip = ({ sx, children }) => {
  const { kycStatus, isCommunityManagementRole, refreshKycStatus } =
    useContext(AppContext);
  const { t } = useTranslation("supplier-kyc");
  const [detailsOpen, setDetailsOpen] = useState(false);

  // Supplier-only: the same person managing a community is not held up by it.
  if (isCommunityManagementRole || !hasKycNotice(kycStatus)) return children ?? null;

  const copyKey = COPY_KEY[kycStatus];
  const tone = TONE[kycStatus];
  // Only the first state is actionable — everywhere else there is nothing the
  // supplier can press, so the chip stays a label.
  const actionable = needsKycDetails(kycStatus) && !children;

  const closeDetails = async () => {
    setDetailsOpen(false);
    // Grow sends nothing when a form is submitted — only when the business is
    // created, which can be days later. Closing the window is the only signal
    // there is, so it is what moves the supplier to "waiting".
    try {
      await apiPostRequest(endpoints.kycFormSubmitted);
    } catch {
      // The chip catches up on the next load; nothing to tell the supplier.
    }
    refreshKycStatus?.();
  };

  return (
    <>
      <CustomTooltip
        title={t(`${copyKey}.tooltip`)}
        tooltipSxStyles={{ maxWidth: "260px", padding: "8px 10px" }}
      >
        {children ? (
          // A disabled button swallows hover events, so the tooltip listens on a
          // wrapper around it.
          <Box component="span" sx={{ display: "inline-flex", width: "100%" }}>
            {children}
          </Box>
        ) : (
          <Box
            component={actionable ? "button" : "span"}
            type={actionable ? "button" : undefined}
            onClick={actionable ? () => setDetailsOpen(true) : undefined}
            sx={{
              ...chipBase,
              ...tone,
              ...(actionable
                ? {
                    border: "none",
                    cursor: "pointer",
                    textDecoration: "underline",
                    textUnderlineOffset: "2px",
                    font: "inherit",
                    fontSize: "11px",
                    fontWeight: 500,
                  }
                : {}),
              ...sx,
            }}
          >
            {t(`${copyKey}.chip`)}
          </Box>
        )}
      </CustomTooltip>

      {actionable && (
        <KycDetailsModal open={detailsOpen} onClose={closeDetails} />
      )}
    </>
  );
};

export default KycStatusChip;
